import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultPaymentFrequency,
  getPaymentModelLabel,
  getPaymentModelOnboardingLabel,
} from '@nxsys/constants/AppConstants';
import {
  OnboardingTaskStatusEnum,
  OnboardingStep,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { PROSPECT_TYPE, titleOptions } from '@nxsys/constants/prospect';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import {
  AgencyProspect,
  Candidate,
  OnboardingTasks,
  Prospect,
} from '@nxsys/store/slices/prospect/types';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { formatStatus } from '@nxsys/utils';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { find, forEach } from 'lodash';
import moment from 'moment';
import getOnboardingStepAndNationalities from './Onboarding/getOnboardingStepAndNationalities';
import clsx from 'clsx';

const showSymbolOnboarding = (tick: string) => {
  switch (tick) {
    case 'YES':
      return <Icons.IconCheckCircle width={20} height={20} />;
    case 'NO':
      return (
        <Icons.IconCheckCircle
          width={20}
          height={20}
          color={defaultTheme.theme.palette.neutral[300]}
        />
      );
    case '':
      return '';
    default:
      return 'N/A';
  }
};

export const getColumns = ({
  messages,
  prospectType,
  handleViewCandidate,
  handleProgressOnboardingByIds,
  handleProgressRegistrationByIds,
  handleDeleteCandidateByIds,
  handleViewAgency,
  handleViewProspect,
  handleResendByIds,
  handleDeleteProspectById,
  handleViewOnboarding,
  isBureau = false,
  isIreland = false,
  isUK = false,
  handleDeleteAgencyByIds,
  isAgency = false,
}: {
  messages: TypedMessages;
  prospectType: PROSPECT_TYPE;
  handleViewCandidate?: (data: Candidate) => void;
  handleProgressOnboardingByIds?: (ids: string[]) => void;
  handleProgressRegistrationByIds?:
    | ((ids: string[]) => void)
    | ((ids: number[]) => void);
  handleDeleteCandidateByIds?: (ids: string[]) => void;
  handleViewAgency?: (data: AgencyProspect) => void;
  handleViewProspect?: (data: Prospect) => void;
  handleResendByIds?: (ids: number[]) => void;
  handleDeleteProspectById?: (id: number) => void;
  handleViewOnboarding?: (data: Prospect) => void;
  isBureau?: boolean;
  isIreland?: boolean;
  isUK?: boolean;
  handleDeleteAgencyByIds?: (ids: string[]) => void;
  isAgency?: boolean;
}): ColumnsType<Candidate | AgencyProspect | Prospect> => {
  if (prospectType === PROSPECT_TYPE.CANDIDATE) {
    return [
      {
        key: 'actions',
        title: messages['common.action'],
        align: 'left',
        render: (record) => {
          return (
            <div className="flex items-center gap-x-3">
              <Tooltip title={messages['common.edit']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleViewCandidate?.(record)}
                >
                  <Icons.IconEdit />
                </span>
              </Tooltip>
              <Tooltip
                title={messages['prospect.candidate.progressToOnboarding']}
              >
                <span
                  className="cursor-pointer"
                  onClick={() => handleProgressOnboardingByIds?.([record.id])}
                >
                  <Icons.IconUserCircle />
                </span>
              </Tooltip>
              <Tooltip
                title={messages['prospect.candidate.progressToRegistration']}
              >
                <span
                  className="cursor-pointer"
                  onClick={() => handleProgressRegistrationByIds?.([record.id])}
                >
                  <Icons.IconArrowRight />
                </span>
              </Tooltip>
              <Tooltip title={messages['common.delete']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleDeleteCandidateByIds?.([record.id])}
                >
                  <Icons.IconTrash
                    color={defaultTheme.theme.palette.red[500]}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
        width: 160,
        fixed: 'left',
      },
      {
        key: 'status',
        title: messages['common.status'],
        dataIndex: 'status',
        align: 'center',
        width: 120,
        render: (record) => {
          const status = record;
          return (
            <span className={`${formatStatus(status)}`}>
              <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
                {formatStatus(status)}
              </AppTagStatus>
            </span>
          );
        },
        fixed: 'left',
      },
      {
        key: 'candidateTitle',
        title: messages['common.title'],
        dataIndex: 'title',
        width: 80,
        render: (record) =>
          record ? find(titleOptions, (op) => op.value === record)?.label : '_',
      },
      {
        key: 'forename',
        title: messages['common.forename'],
        dataIndex: 'forename',
        width: 150,
        sorter: true,
      },
      {
        key: 'surname',
        title: messages['common.surname'],
        dataIndex: 'surname',
        width: 150,
        sorter: true,
      },
      {
        key: 'DOB',
        title: messages['common.birthDate'],
        dataIndex: 'DOB',
        align: 'right',
        width: 120,
        render: (record) =>
          record
            ? moment(record).tz('Europe/London').format('DD/MM/YYYY')
            : null,
      },
      ...(!isBureau
        ? [
            {
              key: 'agencyName',
              title: messages['assignment.detail.agencyName'],
              width: 180,
              sorter: true,
              // A candidate sits against either a live agency or an agency
              // prospect — whichever is set is the one shown, and the BE sorts
              // on the same coalesced name
              render: (_value: unknown, record: any) =>
                record?.agencyName?.name ??
                record?.agencyNameProspect?.name ??
                '',
            },
          ]
        : []),
      ...(!isUK
        ? [
            {
              key: 'paymentModel',
              title: messages['common.paymentModel'],
              dataIndex: 'paymentModel',
              width: 200,
              sorter: true,
              render: (value: PAYMENT_MODEL_ENUM) =>
                getPaymentModelLabel(value, isIreland) || '_',
            },
          ]
        : []),
      {
        key: 'createdAt',
        title: messages['common.dateCreated'] || 'Date Created',
        dataIndex: 'createdAt',
        align: 'right' as const,
        width: 160,
        sorter: true,
        render: (value: string) =>
          value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : null,
      },
      {
        key: 'companyName',
        title: isBureau
          ? messages['common.bureau.companyName']
          : messages['common.companyName'],
        dataIndex: 'companyPayment',
        width: 180,
        sorter: true,
        render: (record) => record?.companyName ?? '-',
      },
      {
        key: 'email',
        title: messages['common.email'],
        dataIndex: 'email',
        width: 200,
      },
      {
        key: 'phone',
        title: messages['common.phone'],
        dataIndex: 'phone',
        width: 150,
      },
      {
        key: 'payFrequency',
        title: messages['common.payFrequency'],
        dataIndex: 'payFrequency',
        width: 140,
        render: (record) =>
          record
            ? find(defaultPaymentFrequency, (op) => op.value === record)?.label
            : '_',
      },
    ];
  }

  if (prospectType === PROSPECT_TYPE.AGENCY) {
    return [
      {
        key: 'actions',
        title: messages['common.action'],
        align: 'left',
        render: (record) => {
          return (
            <div className="flex items-center gap-x-3">
              <Tooltip title={messages['common.edit']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleViewAgency?.(record)}
                >
                  <Icons.IconEdit />
                </span>
              </Tooltip>
              <Tooltip
                title={messages['prospect.candidate.progressToRegistration']}
              >
                <span
                  className="cursor-pointer"
                  onClick={() => handleProgressRegistrationByIds?.([record.id])}
                >
                  <Icons.IconArrowRight />
                </span>
              </Tooltip>
              <Tooltip title={messages['common.delete']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleDeleteAgencyByIds?.([record.id])}
                >
                  <Icons.IconTrash
                    color={defaultTheme.theme.palette.red[500]}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
        width: 140,
        fixed: 'left',
      },
      {
        key: 'name',
        title: messages['common.agencyName'],
        dataIndex: 'name',
        width: 200,
        sorter: true,
      },
      {
        key: 'contactName',
        title: messages['common.contactName'],
        dataIndex: 'contactName',
        width: 180,
        sorter: true,
      },
      {
        key: 'email',
        title: messages['common.email'],
        dataIndex: 'email',
        width: 220,
        sorter: true,
      },
      {
        key: 'phone',
        title: messages['common.phone'],
        dataIndex: 'phone',
        width: 150,
      },
    ];
  }

  if (prospectType === PROSPECT_TYPE.ONBOARDING) {
    return [
      {
        key: 'actions',
        title: messages['common.action'],
        align: 'left',
        render: (record) => {
          if (isAgency) {
            return (
              <ul className="flex justify-center gap-x-4">
                <li onClick={() => handleViewProspect?.(record)}>
                  <Tooltip title="View">
                    <span>
                      <Icons.IconEye />
                    </span>
                  </Tooltip>
                </li>
                <li onClick={() => handleDeleteProspectById?.(record.id)}>
                  <Tooltip title="Delete">
                    <span>
                      <Icons.IconDelete
                        color={defaultTheme.theme.palette.red[500]}
                      />
                    </span>
                  </Tooltip>
                </li>
              </ul>
            );
          }

          return (
            <div className="flex items-center gap-x-3">
              <Tooltip title={messages['common.edit']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleViewProspect?.(record)}
                >
                  <Icons.IconEdit />
                </span>
              </Tooltip>
              {record.userRef ? (
                <Tooltip
                  title={messages['prospect.onboarding.completeOnboarding']}
                >
                  <span
                    className="cursor-pointer"
                    onClick={() => handleViewOnboarding?.(record)}
                  >
                    <Icons.IconFileListCheck />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title={messages['prospect.candidate.progressToRegistration']}
                >
                  <span
                    className="cursor-pointer"
                    onClick={() =>
                      handleProgressRegistrationByIds?.([record.id])
                    }
                  >
                    <Icons.IconArrowRight />
                  </span>
                </Tooltip>
              )}
              {record.userRef && (
                <Tooltip
                  title={
                    record.progressError ||
                    messages['prospect.onboarding.resendLoginDetails']
                  }
                >
                  <span
                    className="cursor-pointer"
                    onClick={() => handleResendByIds?.([record.id])}
                  >
                    <Icons.IconSend
                      color={
                        record.progressError
                          ? defaultTheme.theme.palette.red[500]
                          : undefined
                      }
                    />
                  </span>
                </Tooltip>
              )}
              <Tooltip title={messages['common.delete']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleDeleteProspectById?.(record.id)}
                >
                  <Icons.IconTrash
                    color={defaultTheme.theme.palette.red[500]}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
        width: 160,
        fixed: 'left',
      },
      {
        key: 'onboardingProgress',
        title: messages['common.onboardingProgress'],
        render: (record) => {
          const onboardingTasks = record?.onboardingTasks;
          const { stepsTemp } = getOnboardingStepAndNationalities({
            isBureau,
            isIreland,
          });
          // The ID step is verified through Yoti when the employee has a
          // passport; while it is not completed, the backend's session state
          // says whether it is still processing or has expired (the employee
          // has to verify again). Only trusted when a Yoti session exists.
          const hasYotiSession =
            record?.onboarding?.havePassport === true &&
            !!record?.onboarding?.sessionIdYoti;
          const yotiState = hasYotiSession
            ? String(record?.onboarding?.yotiSessionState ?? '').toUpperCase()
            : '';
          const tasks: {
            index: number;
            key: string;
            completed: boolean;
            icon: React.ReactNode;
            label: string;
            status: string;
            statusClassName: string;
          }[] = [];
          let index = 0;
          const tempSteps: Record<string, { label: string; key: string }> =
            stepsTemp;
          forEach(tempSteps, (_, key) => {
            if (
              onboardingTasks &&
              onboardingTasks[key as keyof OnboardingTasks] !==
                OnboardingTaskStatusEnum.NA
            ) {
              const isStepCompleted = !!(
                onboardingTasks[key as keyof OnboardingTasks] ===
                OnboardingTaskStatusEnum.YES
              );
              const yotiStepState =
                key === OnboardingStep.ADD_ID && !isStepCompleted
                  ? yotiState
                  : '';
              const isYotiExpired = yotiStepState === 'EXPIRED';
              const isYotiError = yotiStepState === 'ERROR';
              const isYotiOngoing = yotiStepState === 'ONGOING';
              tasks.push({
                index,
                key,
                completed: isStepCompleted,
                label: mapOnboardingStepToLabel(
                  key as OnboardingStep,
                  isIreland,
                ),
                icon: mapOnboardingStepToIcon(key as OnboardingStep, {
                  color: isStepCompleted
                    ? defaultTheme.theme.palette.primary[600]
                    : isYotiExpired || isYotiError
                      ? defaultTheme.theme.palette.red[500]
                      : isYotiOngoing
                        ? defaultTheme.theme.palette.yellow[500]
                        : defaultTheme.theme.palette.neutral[500],
                }),
                status: isStepCompleted
                  ? messages['common.completed']
                  : isYotiExpired
                    ? (messages['prospect.onboarding.yotiStatus.expired'] ??
                      'Expired (Please re-verify ID)')
                    : isYotiError
                      ? (messages['prospect.onboarding.yotiStatus.error'] ??
                        'Error (Please re-verify ID)')
                      : isYotiOngoing
                        ? (messages['prospect.onboarding.yotiStatus.ongoing'] ??
                          'Ongoing')
                        : messages['common.pending'],
                statusClassName: isStepCompleted
                  ? 'text-primary-600'
                  : isYotiExpired || isYotiError
                    ? 'text-red-500'
                    : isYotiOngoing
                      ? 'text-yellow-500'
                      : 'text-neutral-300',
              });
              index++;
            }
          });
          return (
            <Tooltip
              title={
                <div className="space-y-[8px]">
                  {tasks.map((item) => (
                    <div
                      key={item.key}
                      className="flex justify-between items-center gap-x-[16px]"
                    >
                      <div className="font-[400] text-[12px] whitespace-nowrap">
                        {item.label}
                      </div>
                      <div
                        className={clsx(
                          'font-[400] text-[12px]',
                          item.statusClassName,
                        )}
                      >
                        {item.status}
                      </div>
                    </div>
                  ))}
                </div>
              }
              placement="bottomLeft"
              styles={{ body: { width: 'fit-content' } }}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {tasks.map((item) => (
                  <span key={item.key} className="shrink-0 inline-flex">
                    {item.icon}
                  </span>
                ))}
              </div>
            </Tooltip>
          );
        },
        width: 484,
      },
      {
        key: 'employeeName',
        title: messages['common.employeeName'],
        width: 180,
        sorter: true,
        render: (record) =>
          `${record?.forename ?? ''} ${record?.surname ?? ''}`,
      },
      {
        key: 'companyName',
        title: isBureau
          ? messages['common.bureau.companyName']
          : messages['common.companyName'],
        dataIndex: 'companyPayment',
        width: 200,
        sorter: true,
        render: (record) => record?.companyName ?? '',
      },
      // An agency user only ever sees their own agency, so the column is noise
      ...((!isBureau && !isAgency
        ? [
            {
              key: 'agencyName',
              title: messages['common.agencyName'],
              dataIndex: 'agency',
              width: 200,
              sorter: true,
              render: (record) => record?.name ?? '',
            },
          ]
        : []) as ColumnsType<Candidate | AgencyProspect | Prospect>),
      {
        key: 'paymentModel',
        title: messages['common.paymentModel'] || 'Payment Model',
        dataIndex: 'paymentModel',
        width: 200,
        sorter: true,
        render: (value: string[]) =>
          value?.length
            ? value
                .map((v) => getPaymentModelOnboardingLabel(v, isIreland) || v)
                .join(', ')
            : '_',
      },
      {
        key: 'createdAt',
        title: messages['common.dateCreated'] || 'Date Created',
        dataIndex: 'createdAt',
        align: 'right' as const,
        width: 160,
        sorter: true,
        render: (value: string) =>
          value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : null,
      },
    ];
  }
  return [];
};

export const getRegistrationCompletedColumns = ({
  isBureau = false,
  isUK = false,
  isIreland = false,
  messages,
}: {
  isBureau?: boolean;
  isUK?: boolean;
  isIreland?: boolean;
  messages: TypedMessages;
}): ColumnsType<EmployeeRecordType> => {
  return [
    {
      key: 'registrationStatus',
      title: 'Registration Status',
      dataIndex: 'registrationStatus',
      align: 'center',
      width: 160,
      render: () => (
        <AppTagStatus className={`tag-popup Success`}>Completed</AppTagStatus>
      ),
    },
    {
      key: 'forename',
      title: 'Forename',
      dataIndex: 'forename',
      width: 150,
    },
    {
      key: 'surname',
      title: 'Surname',
      dataIndex: 'surname',
      width: 150,
    },
    {
      key: 'dob',
      title: 'DOB',
      dataIndex: 'DOB',
      align: 'right',
      width: 120,
      render: (value) => (
        <span>
          {value
            ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
            : null}
        </span>
      ),
    },
    {
      key: 'companyPayment',
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'companyPayment',
      width: 180,
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: (value) => value || '_',
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      width: 150,
      render: (value) => value || '_',
    },
    {
      key: 'payRate',
      title: 'Pay Rate',
      dataIndex: 'prospectOnboarding',
      align: 'right',
      width: 120,
      render: (value) => value?.payRate || '_',
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      width: 140,
      render: (value) =>
        find(defaultPaymentFrequency, (item) => item?.value === value)?.label ||
        '_',
    },
    ...(!isUK
      ? [
          {
            key: 'paymentModel',
            title: 'Payment Model',
            dataIndex: 'paymentModel',
            width: 200,
            render: (value: string) =>
              getPaymentModelOnboardingLabel(value, isIreland),
          },
        ]
      : []),
    {
      key: 'createdBy',
      title: 'Created By',
      dataIndex: 'prospectOnboarding',
      width: 160,
      render: (value) => {
        const createdBy = value?.createdBy;
        return `${createdBy?.forename || ''} ${createdBy?.surname || ''}`;
      },
    },
    {
      key: 'createdAt',
      title: 'Created On',
      dataIndex: 'prospectOnboarding',
      align: 'right',
      width: 130,
      render: (value) => (
        <span>
          {value?.createdAt
            ? moment(value?.createdAt).tz('Europe/London').format('DD/MM/YYYY')
            : null}
        </span>
      ),
    },
    {
      title: 'Add Nationality',
      dataIndex: 'prospectOnboarding',
      width: 150,
      render: (record) => {
        const onboardingTasks = record?.onboardingTasks;
        const onboarding = record?.onboarding;
        return onboardingTasks?.addNationality ? (
          <span className={`${onboarding?.nationality ? 'text' : ''}`}>
            {onboarding?.nationality ||
              showSymbolOnboarding(onboardingTasks.nationality)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      title: 'Verify your ID',
      dataIndex: 'prospectOnboarding',
      width: 130,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addID ? (
          <span className={`color--${onboardingTasks?.addID}`}>
            {showSymbolOnboarding(onboardingTasks?.addID)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      title: 'Add Share Code / EC Check Code',
      dataIndex: 'prospectOnboarding',
      width: 220,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addShareCode ? (
          <span className={`color--${onboardingTasks.addShareCode}`}>
            {showSymbolOnboarding(onboardingTasks.addShareCode)}
          </span>
        ) : (
          '_'
        );
      },
    },
    // Ireland-only onboarding task
    ...(isIreland
      ? [
          {
            title: 'Capture ID Docs',
            dataIndex: 'prospectOnboarding',
            width: 160,
            render: (value: any) => {
              const onboardingTasks = value?.onboardingTasks;
              return onboardingTasks?.addCaptureIdDocs ? (
                <span className={`color--${onboardingTasks.addCaptureIdDocs}`}>
                  {showSymbolOnboarding(onboardingTasks.addCaptureIdDocs)}
                </span>
              ) : (
                '_'
              );
            },
          },
        ]
      : []),
    {
      title: 'Sign Contract',
      dataIndex: 'prospectOnboarding',
      width: 140,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.signContract ? (
          <span className={`color--${onboardingTasks?.signContract}`}>
            {showSymbolOnboarding(onboardingTasks?.signContract)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      title: 'Add Bank Details (Account Name, Account Number, Sort Code)',
      dataIndex: 'prospectOnboarding',
      width: 200,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addBankDetails ? (
          <span className={`color--${onboardingTasks?.addBankDetails}`}>
            {showSymbolOnboarding(onboardingTasks?.addBankDetails)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      title:
        'Add Address Details (Address Line 1,2,3 City/Town, County, Post Code, Country)',
      dataIndex: 'prospectOnboarding',
      width: 250,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addAddressDetails ? (
          <span className={`color--${onboardingTasks?.addAddressDetails}`}>
            {showSymbolOnboarding(onboardingTasks?.addAddressDetails)}
          </span>
        ) : (
          '_'
        );
      },
    },
    // P45 is UK-only paperwork — Ireland never enables these tasks
    ...(!isIreland
      ? [
          {
            title: 'Add P45 Starter Declaration',
            dataIndex: 'prospectOnboarding',
            width: 200,
            render: (value: any) => {
              const onboardingTasks = value?.onboardingTasks;
              return onboardingTasks?.addP45StarterDeclaration ? (
                <span
                  className={`color--${onboardingTasks?.addP45StarterDeclaration}`}
                >
                  {showSymbolOnboarding(
                    onboardingTasks?.addP45StarterDeclaration,
                  )}
                </span>
              ) : (
                '_'
              );
            },
          },
          {
            title: 'Add P45 Details (P45 Gross for Tax and P45 Tax Deducted)',
            dataIndex: 'prospectOnboarding',
            width: 220,
            render: (value: any) => {
              const onboardingTasks = value?.onboardingTasks;
              return onboardingTasks?.addP45Details ? (
                <span className={`color--${onboardingTasks?.addP45Details}`}>
                  {showSymbolOnboarding(onboardingTasks?.addP45Details)}
                </span>
              ) : (
                '_'
              );
            },
          },
        ]
      : []),
    {
      title: 'Add Holiday Pay Method  Advanced, Retained',
      dataIndex: 'prospectOnboarding',
      width: 200,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addHolidayPayMethod ? (
          <span className={`color--${onboardingTasks?.addHolidayPayMethod}`}>
            {showSymbolOnboarding(onboardingTasks?.addHolidayPayMethod)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      title: 'Add Date of Birth',
      dataIndex: 'prospectOnboarding',
      width: 150,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addDOB ? (
          <span className={`color--${onboardingTasks?.addDOB}`}>
            {showSymbolOnboarding(onboardingTasks?.addDOB)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      // NX-5228: Ireland issues a PPS Number, not an NI Number. The onboarding
      // step itself already renames the field via getFieldLabelByCountry and
      // both company Onboarding settings forms relabel the option; this column
      // was the one place left calling it by the UK name.
      title: isIreland ? 'Add PPS Number' : 'Add NI Number',
      dataIndex: 'prospectOnboarding',
      width: 140,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addNINumber ? (
          <span className={`color--${onboardingTasks?.addNINumber}`}>
            {showSymbolOnboarding(onboardingTasks?.addNINumber)}
          </span>
        ) : (
          '_'
        );
      },
    },
    {
      // Ireland's equivalent of the UK Unique Taxpayer Reference is the Tax
      // Reference Number — the same pairing labelConfig maps UTRNo to.
      title: isIreland ? 'Add TRN' : 'Add UTR',
      dataIndex: 'prospectOnboarding',
      width: 120,
      render: (value) => {
        const onboardingTasks = value?.onboardingTasks;
        return onboardingTasks?.addUTR ? (
          <span className={`color--${onboardingTasks?.addUTR}`}>
            {showSymbolOnboarding(onboardingTasks?.addUTR)}
          </span>
        ) : (
          '_'
        );
      },
    },
  ];
};

const mapOnboardingStepToIcon = (step: OnboardingStep, props: any) => {
  switch (step) {
    case OnboardingStep.ADD_NATIONALITY:
      return <Icons.IconGlobe width={20} height={20} {...props} />;
    case OnboardingStep.ADD_ID:
      return <Icons.IconBadge width={20} height={20} {...props} />;
    case OnboardingStep.ADD_SHARE_CODE:
      return <Icons.IconCode width={20} height={20} {...props} />;
    case OnboardingStep.ADD_CAPTURE_ID_DOCS:
      return <Icons.IconMoneyBill width={20} height={20} {...props} />;
    case OnboardingStep.ADD_DOB:
      return <Icons.IconCake width={20} height={20} {...props} />;
    case OnboardingStep.ADD_BANK_DETAILS:
      return <Icons.IconBank width={20} height={20} {...props} />;
    case OnboardingStep.ADD_ADDRESS_DETAILS:
      return <Icons.IconMapPin width={20} height={20} {...props} />;
    case OnboardingStep.ADD_JOB_DESCRIPTION:
      return <Icons.IconShoppingBag width={20} height={20} {...props} />;
    case OnboardingStep.ADD_WORKING_TIME_REGULATION:
      return <Icons.IconClock width={20} height={20} {...props} />;
    case OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION:
      return <Icons.IconBuilding width={20} height={20} {...props} />;
    case OnboardingStep.ADD_P45_STARTER_DECLARATION:
      return <Icons.IconDocument width={20} height={20} {...props} />;
    case OnboardingStep.ADD_P45_DETAILS:
      return <Icons.IconDocumentList width={20} height={20} {...props} />;
    case OnboardingStep.ADD_NI_NUMBER:
      return <Icons.IconIDCard width={20} height={20} {...props} />;
    case OnboardingStep.ADD_HOLIDAY_PAY_METHOD:
      return <Icons.IconCreditCard width={20} height={20} {...props} />;
    case OnboardingStep.ADD_SIGN_CONTRACT:
      return <Icons.IconClipboardEdit width={20} height={20} {...props} />;
    case OnboardingStep.ADD_UTR:
      return <Icons.IconFileText width={20} height={20} {...props} />;
  }
};

const mapOnboardingStepToLabel = (step: OnboardingStep, isIreland = false) => {
  switch (step) {
    case OnboardingStep.ADD_NATIONALITY:
      return 'Nationality';
    case OnboardingStep.ADD_ID:
      return 'ID';
    case OnboardingStep.ADD_SHARE_CODE:
      return 'Share Code';
    case OnboardingStep.ADD_CAPTURE_ID_DOCS:
      return 'Capture ID Docs';
    case OnboardingStep.ADD_DOB:
      return 'DOB';
    case OnboardingStep.ADD_BANK_DETAILS:
      return 'Bank Details';
    case OnboardingStep.ADD_ADDRESS_DETAILS:
      return 'Address Details';
    case OnboardingStep.ADD_JOB_DESCRIPTION:
      return 'Job Title';
    case OnboardingStep.ADD_WORKING_TIME_REGULATION:
      return 'Working Time Regulation';
    case OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION:
      return 'Agency Conduct Regulation';
    case OnboardingStep.ADD_P45_STARTER_DECLARATION:
      return 'P45 Starter';
    case OnboardingStep.ADD_P45_DETAILS:
      return 'P45 Detail';
    case OnboardingStep.ADD_NI_NUMBER:
      return isIreland ? 'PPS Number' : 'NI Number';
    case OnboardingStep.ADD_HOLIDAY_PAY_METHOD:
      return 'Holiday Pay Method';
    case OnboardingStep.ADD_SIGN_CONTRACT:
      return 'Contract';
    case OnboardingStep.ADD_UTR:
      return isIreland ? 'TRN Number' : 'UTR Number';
  }
};
